/**
 * SetupCard — the pre-draft setup exchange in the Chat tab as one quiet
 * card: the document type, the library, and the next step. It replaces the
 * three to five bubbles that exchange used to post ("Selected …", "Got it —
 * …", a next-step hint, 'Selected library "…"', 'Selected "…"') and
 * updates in place as the writer completes or changes a choice.
 *
 * Same card language as RunRibbon: white, hairline border, 8px radius, a
 * status disc, mono row labels.
 *
 * SRP: presentation only. The card's content comes from ``foldSetupRows``.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { SetupDocumentInfo } from "../documentIdentity";
import type { SetupCard as SetupCardModel, SetupCommand } from "../setupCardModel";
import { brandColors, doctypeChipFallback, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

export interface SetupCardProps {
  card: SetupCardModel;
  /** The open document's name and folder (chosen in "Save as", not in chat).
   *  Absent → no Name / Folder rows. */
  document?: SetupDocumentInfo | null;
  /** Take the next step (pick a library / start drafting). Absent → the
   *  next step shows as plain text. */
  onNextStep?: (step: SetupCommand) => void;
}

const useStyles = makeStyles({
  card: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    background: "#fff",
    fontFamily: fonts.body,
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "7px 10px 3px",
  },
  mark: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    boxSizing: "border-box",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    background: statusColors.cool,
  },
  markPending: {
    width: "14px",
    height: "14px",
    background: "#fff",
    border: `1.5px dashed ${inkColors.ink4}`,
  },
  title: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: inkColors.ink2,
    whiteSpace: "nowrap",
  },
  rows: {
    margin: 0,
    padding: "2px 10px 7px 34px",
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    columnGap: "10px",
    rowGap: "3px",
    alignItems: "baseline",
  },
  label: {
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: inkColors.ink4,
    whiteSpace: "nowrap",
  },
  value: {
    margin: 0,
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    minWidth: 0,
    fontSize: "12px",
    lineHeight: 1.35,
    color: inkColors.ink2,
  },
  valueText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  /** Doctype names run long ("Periodic Safety Update / Periodic
   *  Benefit-Risk Evaluation"): wrap to two lines before clipping. */
  valueWrap: {
    whiteSpace: "normal",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  pending: { color: inkColors.ink4, fontStyle: "italic" },
  chip: {
    flexShrink: 0,
    padding: "0 5px",
    borderRadius: "4px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 600,
    lineHeight: "16px",
    background: doctypeChipFallback.bg,
    color: doctypeChipFallback.fg,
  },
  note: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    letterSpacing: "0.04em",
    color: inkColors.ink4,
  },
  next: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    padding: "5px 10px 7px 34px",
    borderTop: `1px solid ${surfaceColors.line}`,
    fontSize: "11.5px",
    color: inkColors.ink3,
  },
  command: {
    padding: 0,
    border: "none",
    background: "none",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    fontWeight: 600,
    color: brandColors.primary,
    cursor: "pointer",
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    ":hover": { textDecorationStyle: "solid" },
  },
  commandText: { fontWeight: 600, color: inkColors.ink2 },
});

/** White check inside the green disc (the Spine's done glyph). */
const CHECK_GLYPH = (
  <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path
      d="M2.5 6.3 5 8.7 9.5 3.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NEXT_LABEL: Record<SetupCommand, string> = {
  library: "Pick or create a library",
  draft: "Start drafting",
};

/** Until the writer names the document, its name and folder are chosen in
 *  "Save as" when drafting starts. */
const UNNAMED_TEXT = "Set when you start drafting";

export const SetupCard: React.FC<SetupCardProps> = ({ card, document, onNextStep }) => {
  const styles = useStyles();
  const complete = !!card.doctype && !!card.library;
  const title = complete ? "Document set up" : "Setting up the document";
  return (
    <div className={styles.card} data-testid="setup-card" data-complete={complete ? "true" : "false"}>
      <div className={styles.head}>
        <span className={mergeClasses(styles.mark, !complete && styles.markPending)} aria-hidden="true">
          {complete ? CHECK_GLYPH : null}
        </span>
        <span className={styles.title}>{title}</span>
      </div>
      <dl className={styles.rows}>
        <dt className={styles.label}>Document type</dt>
        <dd className={styles.value} data-testid="setup-card-doctype">
          {card.doctype ? (
            <>
              {card.doctype.code && <span className={styles.chip}>{card.doctype.code}</span>}
              <span className={mergeClasses(styles.valueText, styles.valueWrap)} title={card.doctype.label}>
                {card.doctype.label}
              </span>
              {card.doctypeChanged && <span className={styles.note}>changed</span>}
            </>
          ) : (
            <span className={mergeClasses(styles.valueText, styles.pending)}>Not chosen yet</span>
          )}
        </dd>
        {document && (
          <>
            <dt className={styles.label}>Name</dt>
            <dd className={styles.value} data-testid="setup-card-name">
              {document.name ? (
                <span className={styles.valueText} title={document.name}>
                  {document.name}
                </span>
              ) : (
                <span className={mergeClasses(styles.valueText, styles.pending)}>{UNNAMED_TEXT}</span>
              )}
            </dd>
            <dt className={styles.label}>Folder</dt>
            <dd className={styles.value} data-testid="setup-card-folder">
              {!document.name && !document.folderLabel ? (
                <span className={mergeClasses(styles.valueText, styles.pending)}>{UNNAMED_TEXT}</span>
              ) : document.folderLabel === undefined ? (
                <span className={mergeClasses(styles.valueText, styles.pending)}>…</span>
              ) : (
                <span className={styles.valueText} title={document.folderLabel ?? "Unfiled"}>
                  {document.folderLabel ?? "Unfiled"}
                </span>
              )}
            </dd>
          </>
        )}
        <dt className={styles.label}>Library</dt>
        <dd className={styles.value} data-testid="setup-card-library">
          {card.library ? (
            <>
              <span className={styles.valueText} title={card.library.name}>
                {card.library.name}
              </span>
              {card.library.created && <span className={styles.note}>new</span>}
              {card.libraryChanged && <span className={styles.note}>changed</span>}
            </>
          ) : (
            <span className={mergeClasses(styles.valueText, styles.pending)}>Not chosen yet</span>
          )}
        </dd>
      </dl>
      {card.next && (
        <div className={styles.next} data-testid="setup-card-next">
          <span>Next:</span>
          {onNextStep ? (
            <button type="button" className={styles.command} onClick={() => card.next && onNextStep(card.next)}>
              {NEXT_LABEL[card.next]}
            </button>
          ) : (
            <span className={styles.commandText}>{NEXT_LABEL[card.next]}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default SetupCard;
